import { BillingChannel, Permission, SubscriptionStatus, User } from '@localstack/types';
import { ExternalLink } from '@localstack/constants';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  SubscriptionService,
  OrganizationService,
  UserService,
  VALIDATION_RULES,
  hashEmail,
  useApiEffect,
  useApiGetter,
  useSnackbar,
} from '@localstack/services';

import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  BrightnessAlertFilled,
  ButtonWithDialog,
  ControlledPhoneField,
  ControlledTextField,
  DangerZoneActions,
  ProgressButton,
  WarningContainer,
} from '@localstack/ui';

import { useAuthProvider } from '~/hooks/useAuthProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    avatar: {
      width: theme.spacing(10),
      height: theme.spacing(10),
      margin: theme.spacing(0, 'auto'),
      boxShadow: theme.shadows[1],
      marginBottom: theme.spacing(1),
    },
    switch: {
      display: 'flex',
      justifyContent: 'center',
    },
  }),
);

const DELETE_ACCOUNT_TEXT = 'Delete my account';
export const Profile = (): ReactElement => {
  const classes = useStyles();
  const { userInfo, can, isWorkspaceAdmin } = useAuthProvider();
  const [confirmText, setConfirmText] = useState<string>('');

  const { user } = userInfo || {};

  const {
    updateAccount,
    deleteUser,
    isLoading: isUserMutating,
  } = useApiEffect(UserService, ['updateAccount', 'deleteUser'], {
    revalidate: ['getUser'],
  });

  const { data: orgStats } = useApiGetter(OrganizationService, 'getOrganizationStats', []);

  const { data: subscriptions, isLoading: isSubscriptionLoading } = useApiGetter(
    SubscriptionService,
    'listSubscriptions',
    [],
    {
      suppressErrors: true,
      enable: can(Permission.READ_SUBSCRIPTIONS),
    },
  );

  const { showSnackbar } = useSnackbar();

  const emailHash = hashEmail(user?.email ?? '');
  const userForm = useForm<{ user: User }>({ mode: 'all' });
  const { dirtyFields } = userForm.formState;

  const onSubmitUserInfo = useCallback(
    async (data: { user: Partial<User> }) => {
      const userUpdates = Object.keys(dirtyFields.user || {}).reduce(
        (memo, field) => ({ ...memo, [field]: data.user[field as keyof typeof data.user] }),
        {},
      );

      const res = await updateAccount(userUpdates as User);
      if (res) {
        showSnackbar({
          message: 'Profile updated',
          severity: 'success',
        });
      } else {
        showSnackbar({
          message: 'Failed to update profile',
          severity: 'error',
        });
      }
    },
    [dirtyFields, updateAccount, showSnackbar],
  );

  // Set initial form values
  useEffect(() => {
    if (user) {
      userForm.reset({ user });
    }
  }, [user]);

  const noOtherAdmin = isWorkspaceAdmin && orgStats?.num_admin_members === 1;

  const hasActivePayingSubscriptions = useMemo(
    () =>
      subscriptions?.some(
        (sub) =>
          sub.status === SubscriptionStatus.ACTIVE &&
          !sub.cancel_at_period_end &&
          sub.billing_channel != BillingChannel.NONE,
      ),
    [subscriptions],
  );

  const shouldDeleteSubFirst = noOtherAdmin && hasActivePayingSubscriptions;
  const doesNotTextMatch = useMemo(() => confirmText !== DELETE_ACCOUNT_TEXT, [confirmText]);

  return (
    <Grid container spacing={2}>
      <Grid item md={8} xs={12}>
        {/* personal info */}
        <form onSubmit={userForm.handleSubmit(onSubmitUserInfo)}>
          <Card>
            <CardHeader title="Personal Info" />
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <TextField label="Email" fullWidth variant="outlined" value={user?.email ?? ''} disabled />
                </Grid>
                <Grid item md={6} xs={12}>
                  <ControlledTextField
                    control={userForm.control}
                    name="user.firstname"
                    label="First Name"
                    placeholder="First Name"
                    fullWidth
                    required
                    variant="outlined"
                    rules={VALIDATION_RULES.safeStringRequired}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <ControlledTextField
                    control={userForm.control}
                    name="user.lastname"
                    label="Last Name"
                    placeholder="Last Name"
                    fullWidth
                    required
                    variant="outlined"
                    rules={VALIDATION_RULES.safeStringRequired}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <ControlledPhoneField
                    control={userForm.control}
                    label="Phone"
                    name="user.phone"
                    fullWidth
                    variant="outlined"
                    rules={VALIDATION_RULES.phone}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <ControlledTextField
                    control={userForm.control}
                    label="Github Username"
                    name="user.github_username"
                    fullWidth
                    variant="outlined"
                    rules={VALIDATION_RULES.safeStringOptional}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <ControlledTextField
                    control={userForm.control}
                    label="Company"
                    name="user.company"
                    fullWidth
                    variant="outlined"
                    rules={VALIDATION_RULES.safeStringOptional}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <ControlledTextField
                    control={userForm.control}
                    label="Job Title"
                    name="user.job_title"
                    fullWidth
                    variant="outlined"
                    rules={VALIDATION_RULES.safeStringOptional}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <ProgressButton color="primary" variant="contained" loading={isUserMutating} type="submit">
                Save Changes
              </ProgressButton>
            </CardActions>
          </Card>
        </form>
      </Grid>

      {/* gravatar */}
      <Grid item md={4} xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={12}>
            <Card>
              <CardContent>
                <Box p={3}>
                  <Avatar
                    className={classes.avatar}
                    alt={user?.name || ''}
                    src={`https://www.gravatar.com/avatar/${emailHash}?d=db`}
                  />
                  <Typography variant="caption" align="center" component="div">
                    Profile photo is powered by{' '}
                    <Link href={ExternalLink.EXTERNAL_GRAVATAR} target="__blank" underline="hover">
                      Gravatar
                    </Link>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Card>
              <CardHeader title="Delete Account" titleTypographyProps={{ color: 'error' }} sx={{ pb: 0, mb: -1 }} />{' '}
              <DangerZoneActions>
                <ButtonWithDialog
                  componentType="DangerZoneAction"
                  primaryText={
                    shouldDeleteSubFirst
                      ? 'Your account is currently the sole owner of this workspace which has active subscriptions.'
                      : 'Permanently remove your account and all of its contents. This action is not reversible.'
                  }
                  actionText={isSubscriptionLoading || shouldDeleteSubFirst ? undefined : 'Delete'}
                  onClick={deleteUser}
                  onBeforeShow={() => setConfirmText('')}
                  disableConfirmButton={isSubscriptionLoading || shouldDeleteSubFirst || doesNotTextMatch}
                  confirmButtonText="Delete Account"
                  dialogTitle="Delete Account"
                  dialogContent={
                    <Stack spacing={2} pt={1}>
                      <Typography>
                        LocalStack will delete all of your tokens, activity, and all other resources belonging to your
                        account.
                      </Typography>
                      <Box>
                        <Typography>{`To verify, type "${DELETE_ACCOUNT_TEXT}" below:`}</Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={confirmText}
                          onChange={(event) => setConfirmText(event.target.value)}
                          placeholder={DELETE_ACCOUNT_TEXT}
                        />
                      </Box>
                    </Stack>
                  }
                />
              </DangerZoneActions>
              {shouldDeleteSubFirst && (
                <Box px={2} pb={2}>
                  <WarningContainer icon={<BrightnessAlertFilled color="inherit" />} sx={{ alignItems: 'flex-start' }}>
                    <Typography variant="body1" color="inherit">
                      You must transfer ownership or cancel the active subscriptions of this workspace before you can
                      delete your account.
                    </Typography>
                  </WarningContainer>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Profile;
